export const DIET_PATTERN_OPTIONS = [
	{ value: 'balanced_mixed', label: 'Balanced mixed diet' },
	{ value: 'high_protein', label: 'High protein focus' },
	{ value: 'lower_carb', label: 'Lower carb approach' },
	{ value: 'plant_forward', label: 'Plant-forward' },
	{ value: 'fast_food_and_sweets', label: 'Mostly fast food & candy' },
	{ value: 'still_figuring_it_out', label: 'Still figuring it out' }
];

export const EXERCISE_FREQUENCY_OPTIONS = [
	{ value: 'rarely_active', label: 'Rarely active' },
	{ value: 'one_to_two_per_week', label: '1-2 workouts per week' },
	{ value: 'three_to_four_per_week', label: '3-4 workouts per week' },
	{ value: 'five_plus_per_week', label: '5+ workouts per week' }
];

import { GOAL_CATEGORY_OPTIONS, GOAL_CATEGORY_UNSET, isGoalCategorySelected } from './fitnessProfileGoals';
import { formatHeightDisplay, parseHeightDisplay } from '../utils/heightDisplay';

export { GOAL_CATEGORY_OPTIONS };

export const EXERCISE_INTENSITY_OPTIONS = [
	{ value: 'low', label: 'Low' },
	{ value: 'moderate', label: 'Moderate' },
	{ value: 'high', label: 'High' }
];

export function labelForOption(options, value) {
	return options.find((option) => option.value === value)?.label || value;
}

export function accountOwnerProfileIdentity(user) {
	if (!user) return { name: '', email: '' };

	return {
		name: user.first_name?.trim() || '',
		email: user.email || ''
	};
}

export function computeAgeFromDateOfBirth(isoDate) {
	if (!isoDate) return null;
	const dob = new Date(`${isoDate}T12:00:00`);
	if (Number.isNaN(dob.getTime())) return null;
	const today = new Date();
	let age = today.getFullYear() - dob.getFullYear();
	const monthDelta = today.getMonth() - dob.getMonth();
	if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < dob.getDate())) {
		age -= 1;
	}
	return age;
}

export function parseBloodPressureString(raw) {
	const trimmed = String(raw || '').trim();
	if (!trimmed) return null;
	const match = trimmed.match(/^(\d{2,3})\s*\/\s*(\d{2,3})$/);
	if (!match) return null;
	return {
		systolic: Number.parseInt(match[1], 10),
		diastolic: Number.parseInt(match[2], 10)
	};
}

export function buildCreateFitnessProfileGoalsPayload(formData) {
	return (formData.goals || [])
		.filter((goal) => goal.outcome?.trim() || goal.targetMetric?.trim() || goal.targetDate)
		.map((goal) => ({
			outcome: goal.outcome.trim(),
			target_metric: goal.targetMetric.trim(),
			target_date: goal.targetDate,
			category: isGoalCategorySelected(goal.category) ? goal.category : undefined
		}));
}

export function buildInviteOnlyFitnessProfilePayload(formData, { includeFitnessHubsSetting = false } = {}) {
	const payload = {
		name: formData.name.trim(),
		email: formData.email.trim(),
		invite_only: true
	};

	if (includeFitnessHubsSetting) {
		payload.fitness_hubs_only = Boolean(formData.fitnessHubsOnly);
	}

	return payload;
}

export function buildCompleteIntakePayload(formData) {
	const payload = {
		date_of_birth: formData.dateOfBirth,
		height_display: formData.height.trim(),
		questionnaire: {
			diet_pattern: formData.dietPattern,
			exercise_frequency: formData.exerciseFrequency,
			exercise_intensity: formData.exerciseIntensity,
			sleep_hours: Number(formData.sleepHours),
			diet_notes: formData.dietNotes.trim(),
			habit_notes: formData.habitNotes.trim(),
			equipment_access: formData.equipmentAccess.trim()
		},
		goals: buildCreateFitnessProfileGoalsPayload(formData)
	};

	const pounds = Number(formData.weight);
	if (Number.isFinite(pounds) && pounds > 0) {
		payload.initial_weight_pounds = pounds;
	}

	const bp = parseBloodPressureString(formData.bloodPressure);
	if (bp) {
		payload.initial_blood_pressure = bp;
	}

	return payload;
}

export function buildCreateFitnessProfilePayload(
	formData,
	{ includeFitnessHubsSetting = false, setAsPrimary = false } = {}
) {
	const payload = {
		name: formData.name.trim(),
		email: formData.email.trim(),
		date_of_birth: formData.dateOfBirth,
		height_display: formData.height.trim(),
		set_as_primary: setAsPrimary || Boolean(formData.isPrimary),
		questionnaire: {
			diet_pattern: formData.dietPattern,
			exercise_frequency: formData.exerciseFrequency,
			exercise_intensity: formData.exerciseIntensity,
			sleep_hours: Number(formData.sleepHours),
			diet_notes: formData.dietNotes.trim(),
			habit_notes: formData.habitNotes.trim(),
			equipment_access: formData.equipmentAccess.trim()
		},
		goals: buildCreateFitnessProfileGoalsPayload(formData)
	};

	if (includeFitnessHubsSetting) {
		payload.fitness_hubs_only = Boolean(formData.fitnessHubsOnly);
	}

	const pounds = Number(formData.weight);
	if (Number.isFinite(pounds) && pounds > 0) {
		payload.initial_weight_pounds = pounds;
	}

	const bp = parseBloodPressureString(formData.bloodPressure);
	if (bp) {
		payload.initial_blood_pressure = bp;
	}

	return payload;
}

export function profileToEditFormData(profile) {
	const questionnaire = profile?.current_questionnaire || {};

	return {
		name: profile?.name || '',
		email: profile?.email || '',
		wednesdayWeighInRemindersEnabled: profile?.wednesday_weigh_in_reminders_enabled !== false,
		isPrimary: Boolean(profile?.is_primary),
		dateOfBirth: profile?.date_of_birth || '',
		height: profile?.height_display || profile?.height || '',
		dietPattern: questionnaire.diet_pattern || '',
		exerciseFrequency: questionnaire.exercise_frequency || '',
		exerciseIntensity: questionnaire.exercise_intensity || '',
		sleepHours: questionnaire.sleep_hours != null ? String(questionnaire.sleep_hours) : '',
		dietNotes: questionnaire.diet_notes || '',
		habitNotes: questionnaire.habit_notes || '',
		equipmentAccess: questionnaire.equipment_access || ''
	};
}

export function buildUpdateFitnessProfilePayload(formData, { isPrimary = false } = {}) {
	const payload = {
		wednesday_weigh_in_reminders_enabled: Boolean(formData.wednesdayWeighInRemindersEnabled),
		date_of_birth: formData.dateOfBirth,
		height_display: formData.height.trim(),
		questionnaire: {
			diet_pattern: formData.dietPattern,
			exercise_frequency: formData.exerciseFrequency,
			exercise_intensity: formData.exerciseIntensity,
			sleep_hours: Number(formData.sleepHours),
			diet_notes: formData.dietNotes.trim(),
			habit_notes: formData.habitNotes.trim(),
			equipment_access: formData.equipmentAccess.trim()
		}
	};

	if (!isPrimary) {
		payload.name = formData.name.trim();
		payload.email = formData.email.trim();
	}

	return payload;
}

export function validateFitnessProfileEditForm(formData, { isPrimary = false } = {}) {
	const errors = {};

	if (!isPrimary) {
		if (!formData.name.trim()) errors.name = 'Name is required.';
		if (!formData.email.trim()) {
			errors.email = 'Email is required for challenge reminders.';
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
			errors.email = 'Enter a valid email address.';
		}
	}
	if (!formData.dateOfBirth) errors.dateOfBirth = 'Date of birth is required.';
	if (!formatHeightDisplay(parseHeightDisplay(formData.height))) {
		errors.height = 'Choose your height.';
	}
	if (!formData.dietPattern) errors.dietPattern = 'Select a diet pattern.';
	if (!formData.exerciseFrequency) errors.exerciseFrequency = 'Select exercise frequency.';
	if (!formData.exerciseIntensity) errors.exerciseIntensity = 'Select exercise intensity.';

	const sleep = Number(formData.sleepHours);
	if (!Number.isFinite(sleep) || sleep < 1 || sleep > 14) {
		errors.sleepHours = 'Enter sleep between 1 and 14 hours.';
	}

	return errors;
}
